import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../utils/axiosAuth";
import { User, Users } from "src/@types/users";
import { RootState } from "../store";
import useAuthToken from "../useAuthToken";

interface InitialState {
  users?: Array<User>;
  total?: number;
  user: User | undefined;
  isLoading: boolean;
  msg: string | undefined;
}

export const getUsers: any = createAsyncThunk<
  Users,
  { page: number; perPage: number }
>("users/getUsers", async ({ page = 1, perPage = 10 }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.get(
    `/sub-users?page=${page}&perPage=${perPage}`,
    config
  );
  return response.data;
});

export const getUser: any = createAsyncThunk<User, { id: string }>(
  "users/getUser",
  async ({ id }, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.get(`/sub-users/${id}`, config);
    return response.data;
  }
);

export const postNewUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    name: string;
    email: string;
    phone: string;
    password: string;
    roles: string[];
    street?: string;
    number?: string;
    city?: string;
    uf?: string;
    cpf?: string;
    picture?: string;
  }
>(
  "users/registerUser",
  async (
    {
      name,
      email,
      phone,
      password,
      roles,
      street,
      number,
      city,
      uf,
      cpf,
      picture,
    },
    { getState }
  ) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.post(
    "/sub-users",
    {
      name,
      email,
      phone,
      password,
      roles,
      street,
      number,
      city,
      uf,
      cpf,
      picture,
    },
    config
  );
  return response.data;
});

export const putUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    name: string;
    email: string;
    phone: string;
    roles: string[];
    password?: string;
    street?: string;
    number?: string;
    city?: string;
    uf?: string;
    cpf?: string;
    picture?: string;
  }
>(
  "users/updateUser",
  async (
    {
      id,
      name,
      email,
      phone,
      roles,
      password,
      street,
      number,
      city,
      uf,
      cpf,
      picture,
    },
    { getState }
  ) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const payload: any = {
      name,
      email,
      phone,
      roles,
      street,
      number,
      city,
      uf,
      cpf,
      picture,
    };
    if (password) {
      payload.password = password;
    }
    const response = await axios.put(`/sub-users/${id}`, payload, config);
    return response.data;
  }
);

export const blockUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    item: any;
  }
>("users/blockUser", async ({ id, item }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.put(`/sub-users/${id}`, { ...item }, config);
  return response.data;
});

export const deleteUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
  }
>("users/deleteUser", async ({ id }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.delete(`/sub-users/${id}`, config);
  return response.data;
});

const initialState: InitialState = {
  users: [],
  total: 0,
  user: undefined,
  isLoading: false,
  msg: "",
};

const usersSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUser.pending, (state) => {
        state.isLoading = true;
        state.user = undefined;
        state.msg = undefined;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload: any = action.payload;
        state.user = payload?.user || payload;
        state.msg = undefined;
      })
      .addCase(getUser.rejected, (state) => {
        state.isLoading = false;
        state.user = undefined;
        state.msg = "Erro ao buscar usuário";
      })
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.msg = undefined;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload: any = action.payload;
        const list =
          Array.isArray(payload)
            ? payload
            : payload?.data ||
              payload?.users ||
              payload?.items ||
              [];
        state.users = list;
        state.total =
          payload?.total ||
          payload?.pagination?.total ||
          list.length;
        state.msg = undefined;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.msg = "Error ao fazer requisição";
      })
      .addCase(postNewUser.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(postNewUser.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Usuário registrado!";
      })
      .addCase(postNewUser.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao registrar usuário!";
      })
      .addCase(putUser.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(putUser.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Usuário atualizado!";
      })
      .addCase(putUser.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao atualizar usuário!";
      });
    // .addCase(postNewPassword.pending, (state) => {
    //   state.isLoading = true;
    //   state.messageRecovery = "";
    // })
    // .addCase(postNewPassword.fulfilled, (state) => {
    //   state.isLoading = false;
    //   state.messageRecovery = "senha alterada";
    // })
    // .addCase(postNewPassword.rejected, (state) => {
    //   state.isLoading = false;
    //   state.messageRecovery = "Erro ao alterar senha";
    // })
    // .addCase(postRefreshToken.pending, (state) => {
    //   state.isLoading = true;
    //   state.messageRecovery = "";
    // })
    // .addCase(postRefreshToken.fulfilled, (state, action) => {
    //   state.isLoading = false;
    //   state.accessToken = action.payload.accessToken;
    // })
    // .addCase(postRefreshToken.rejected, (state) => {
    //   state.accessToken = "";
    //   state.refreshToken = "";
    //   state.userReturn = initialState.userReturn;
    //   state.isLoading = false;
    //   state.error = "";
    //   state.messageRecovery = "";
    // });
  },
});

// export const {  } = usersSlice.actions;

export default usersSlice.reducer;
